// HeroSection.jsx
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../utils/LanguageContext';
import { motion } from 'framer-motion';
import { subscribeToMarathiContent } from '../apiService';
import rsBlogOnlyBg from "../assets/images/rs-blog_only_bg.webp";
import rsOnlyFace from "../assets/images/rs_onlyface.webp";
import blurbox from "../assets/images/blurbox.webp";

const HeroSection = () => {
  const { language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallDevice, setIsSmallDevice] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    thoughts: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Detect specific small devices (iPhone, Pixel, etc)
      const smallDevice = mobile && window.innerHeight < 700;
      setIsSmallDevice(smallDevice);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-show video popup after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVideoPopupOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Animation variants for the moving text
  const textVariants = {
    animate: {
      x: ['0%', '-100%'],
      transition: {
        x: {
          repeat: Infinity,
          repeatType: "loop",
          duration: 15,
          ease: "linear"
        }
      }
    }
  };

  const marathiText = "माझा मार्ग मधला आहे . विकास तर हवाच . पण विचारांची झाडे तोडून नाही. AI तर हवेच पण NI ला ( nature's Intellegence ) डावलून नाही";
  const englishText = "My path is in the middle. Development is certainly needed. But not by cutting down the trees of thoughts. AI is certainly needed but not by defeating NI (Nature's Intelligence)";

  const openPopup = () => {
    setIsPopupOpen(true);
    setSubmitMessage('');
    setSubmitError('');
  };
  
  const closePopup = () => {
    setIsPopupOpen(false);
    setFormData({ name: '', email: '', thoughts: '' });
    setSubmitMessage('');
    setSubmitError('');
  };

  const openVideoPopup = () => setIsVideoPopupOpen(true);
  const closeVideoPopup = () => setIsVideoPopupOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      setSubmitError(language === 'MR' ? 'नाव आणि ईमेल आवश्यक आहे' : 'Name and email are required');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitMessage('');

    try {
      const result = await subscribeToMarathiContent({
        name: formData.name.trim(),
        email: formData.email.trim(),
        thoughts: formData.thoughts.trim(),
        type: 'content'
      });

      if (result.success) {
        setSubmitMessage(language === 'MR' ? 
          'धन्यवाद! तुम्ही यशस्वीरित्या सामील झालात.' : 
          'Thank you! You have successfully joined.'
        );
        setFormData({ name: '', email: '', thoughts: '' });
        
        // Close popup after 2 seconds
        setTimeout(() => {
          closePopup();
        }, 2000);
      } else {
        setSubmitError(result.message || (language === 'MR' ? 
          'काहीतरी चूक झाली. कृपया पुन्हा प्रयत्न करा.' : 
          'Something went wrong. Please try again.'
        ));
      }
    } catch (error) {
      setSubmitError(language === 'MR' ? 
        'काहीतरी चूक झाली. कृपया पुन्हा प्रयत्न करा.' : 
        'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background layer */}
      <img 
        src={rsBlogOnlyBg} 
        alt="Hero Background" 
        className="w-full h-full object-cover object-center"
      />

      {/* Animated language text - moved to earlier z-index */}
      <motion.div
        className={`absolute whitespace-nowrap ${
          isSmallDevice ? 'top-[8%]' :  
          isMobile ? 'top-[10%]' : 'top-[calc(25%-6rem)]'
        }`}
        variants={textVariants}
        animate="animate"
        style={{ 
          width: '200%',
          left: '0%',
          zIndex: 0
        }}
      >
        <span className={`${
          isSmallDevice ? 'text-6xl' :  
          isMobile ? 'text-7xl' : 'text-8xl'
        } font-bold text-white drop-shadow-2xl mr-8`}>
          {language === 'MR' ? marathiText : englishText}
        </span>
        {/* Duplicate for seamless looping */}
        <span className={`${
          isSmallDevice ? 'text-6xl' :  
          isMobile ? 'text-7xl' : 'text-8xl'
        } font-bold text-white drop-shadow-2xl ml-8`}>
          {language === 'MR' ? marathiText : englishText}
        </span>
      </motion.div>

      {/* RS Face layer - moved up slightly */}
      <div className="absolute inset-0 flex items-center justify-center" style={{
        transform: isMobile ? 'translateY(-5%)' : 'none' // Moved up 5%
      }}>
        <img 
          src={rsOnlyFace} 
          alt="RS Face"
          className={`w-full h-full object-contain object-center ${
            isMobile ? 'scale-[1.7]' : ''
          }`}
          style={{
            transform: isMobile ? 'scale(1.7)' : 'none'
          }}
        />
      </div>

      {/* Text and buttons */}
      <div className={`absolute bottom-0 left-0 right-0 flex flex-col md:flex-row justify-between items-start md:items-end p-4 md:px-16 z-10 backdrop-blur-sm ${
        isSmallDevice ? 'h-[45%]' : 
        isMobile ? 'h-[40%]' : 'h-64'
      }`}>
        <h1 className={`text-white ${
          isSmallDevice ? 'text-3xl mb-6' : 
          isMobile ? 'text-4xl mb-4' : 'text-6xl mb-16'
        } font-bold drop-shadow-xl`}>
          {language === 'MR' ? 'राजेंद्र शेंडे चा ब्लॉग' : "Rajendra Shende's Blog"}
        </h1>

        <div className={`flex flex-col ${
          isMobile ? 'w-full' : 'items-start max-w-xl'
        } mb-4 md:mb-10`}>
          <div className={`text-white ${
            isSmallDevice ? 'text-xs' : 
            isMobile ? 'text-sm' : 'text-lg'
          } mb-2 drop-shadow text-left`}>
            {language === 'MR' ? (
              <>
                <p className="mb-1">"माझी गोष्ट माझ्या जन्मापासून सुरु होत नाही. ती सुरु होते ,</p>
                <p className="mb-1">माझ्या पुनर्जन्मापासून !आणि गम्मत म्हणजे माझा </p>
                <p> पुनर्जन्म माझ्या याच आयुष्यात बऱ्याच वेळा झाला! त्याची  हि कहाणी"</p>
              </>
            ) : (
              <>
                <p className="mb-1">"It doesn't start from when I was born.</p>
                <p className="mb-1">But it starts from when I was reborn.</p>
                <p>And it happened many times"</p>
              </>
            )}
          </div>

          {/* Simple join button */}
          <button 
            onClick={openPopup}
            className={`bg-white text-red-700 font-bold px-6 py-3 rounded-md hover:bg-gray-100 transition-colors duration-300 ${
              isMobile ? 'w-full' : 'whitespace-nowrap'
            }`}
          >
            {language === 'MR' ? 'इतर वाचकांमध्ये सामील होण्यासाठी क्लिक करा' : 'Click to join other readers'}
          </button>
        </div>
      </div>

      {/* Blur box image - positioned at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3">
        <img 
          src={blurbox} 
          alt="Blur Box"
          className="w-full h-full object-cover object-bottom pointer-events-none"
          style={{ zIndex: 2 }}
        />
      </div>

      {/* Optimized Video Popup */}
      {isVideoPopupOpen && (
        <>
          {/* Simple backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 z-40 transition-opacity duration-300"
            onClick={closeVideoPopup}
          />
          
          {/* Sleek video popup with CSS animations */}
          <div 
            className={`fixed left-4 top-1/2 transform -translate-y-1/2 z-50 transition-all duration-500 ease-out ${
              isVideoPopupOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
            } ${isMobile ? 'w-[calc(100%-2rem)] max-w-sm' : 'w-96'}`}
            style={{
              animation: isVideoPopupOpen ? 'slideInLeft 0.5s ease-out' : 'slideOutLeft 0.3s ease-in'
            }}
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              {/* Beautiful header */}
              <div className="bg-gradient-to-r from-red-600 to-red-800 p-4 relative">
                <button 
                  onClick={closeVideoPopup}
                  className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <h3 className="text-lg font-bold text-white mb-1 pr-8">
                  {language === 'MR' ? 'हा व्हिडिओ तुम्ही आधी पाहिला आहे का?' : 'Have you watched this video before?'}
                </h3>
                
                <div className="flex items-center text-white/90 text-xs">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  {language === 'MR' ? 'पुणे रत्न पुरस्कार' : 'Pune Ratna Award'}
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-4">
                <p className="text-sm text-gray-700 font-medium leading-relaxed line-clamp-3">
                  {language === 'MR' ? 
                    'लोकशाही मराठी पुणे रत्न 2025 | राजेंद्र शेंडे यांचा "लोकशाही मराठी" पुणे रत्न पुरस्काराने सन्मान' : 
                    'Lokshahi Marathi PuneRatna 2025 | राजेंद्र शेंडे यांचा "लोकशाही मराठी" पुणे रत्न पुरस्काराने सन्मान'
                  }
                </p>
                
                {/* Video container */}
                <div className="relative rounded-xl overflow-hidden shadow-lg bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/oHLqQL3WL4s"
                    title="Lokshahi Marathi PuneRatna 2025"
                    className="w-full h-48 rounded-xl"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-2">
                  <button 
                    onClick={closeVideoPopup}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 text-sm"
                  >
                    {language === 'MR' ? 'बंद करा' : 'Close'}
                  </button>
                  <button 
                    onClick={() => window.open('https://youtu.be/oHLqQL3WL4s?feature=shared', '_blank')}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 text-sm shadow-lg"
                  >
                    {language === 'MR' ? 'YouTube वर पहा' : 'Watch on YouTube'}
                  </button>
                </div>
              </div>
              
              {/* Bottom accent */}
              <div className="h-1 bg-gradient-to-r from-red-600 to-red-800"></div>
            </div>
          </div>
        </>
      )}

      {/* Subscription Popup Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            {/* Close button */}
            <button 
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              disabled={isSubmitting}
            >
              ×
            </button>
            
            {/* Popup content */}
            <h2 className="text-2xl font-bold text-red-700 mb-4">
              {language === 'MR' ? 'सामील व्हा' : 'Join Us'}
            </h2>
            
            {/* Question text inside popup */}
            <p className="text-gray-700 mb-6 text-lg font-medium">
              {language === 'MR' ? 
                'माझ्या जगभरच्या चाहत्यांमध्ये सामील व्हायचं? तुमचा अभिप्राय जरूर कळवा.' : 
                'Want to join my global fanbase? Please share your thoughts.'
              }
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={language === 'MR' ? 'नाव......' : 'Name...'} 
                maxLength={100}
                className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={isSubmitting}
                required
              />
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={language === 'MR' ? 'ईमेल पत्ता......' : 'Email address...'} 
                maxLength={254}
                className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                disabled={isSubmitting}
                required
              />
              <textarea 
                name="thoughts"
                value={formData.thoughts}
                onChange={handleInputChange}
                placeholder={language === 'MR' ? 'तुमचे विचार......' : 'Your thoughts...'} 
                rows="4"
                maxLength={1000}
                className="px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                disabled={isSubmitting}
              />
              
              {/* Success message */}
              {submitMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                  {submitMessage}
                </div>
              )}
              
              {/* Error message */}
              {submitError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {submitError}
                </div>
              )}
              
              <div className="flex gap-3">
                <button 
                  type="button"
                  onClick={closePopup}
                  className="flex-1 bg-gray-500 text-white font-bold py-3 px-4 rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {language === 'MR' ? 'रद्द करा' : 'Cancel'}
                </button>
                <button 
                  type="submit"
                  className="flex-1 bg-red-700 text-white font-bold py-3 px-4 rounded-md hover:bg-red-800 transition-colors disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 
                    (language === 'MR' ? 'सबमिट करत आहे...' : 'Submitting...') :
                    (language === 'MR' ? 'सबमिट करा' : 'Submit')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Simple floating video button */}
      <button
        onClick={openVideoPopup}
        className="fixed left-4 top-1/2 transform -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-xl transition-all duration-300 z-30"
        title={language === 'MR' ? 'व्हिडिओ पहा' : 'Watch Video'}
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
        </svg>
      </button>

      {/* CSS Animations for smooth performance */}
      <style jsx>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%) translateY(-50%);
            opacity: 0;
          }
          to {
            transform: translateX(0) translateY(-50%);
            opacity: 1;
          }
        }
        
        @keyframes slideOutLeft {
          from {
            transform: translateX(0) translateY(-50%);
            opacity: 1;
          }
          to {
            transform: translateX(-100%) translateY(-50%);
            opacity: 0;
          }
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default HeroSection;